import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  CreateFormSubmissionDTO,
  FormListQueryDTO,
  TemplateListQueryDTO,
} from "@/modules/document-portal/domain/contracts";
import { documentsApi } from "./client";

export const documentsKeys = {
  forms: (q: Partial<FormListQueryDTO>) => ["documents", "forms", q] as const,
  formCategories: ["documents", "forms", "categories"] as const,
  formDetail: (id: string) => ["documents", "forms", "detail", id] as const,
  templates: (q: Partial<TemplateListQueryDTO>) =>
    ["documents", "templates", q] as const,
  contracts: ["documents", "contracts"] as const,
  contractDetail: (id: string) =>
    ["documents", "contracts", "detail", id] as const,
  policies: ["documents", "policies"] as const,
  policyDetail: (id: string) =>
    ["documents", "policies", "detail", id] as const,
  recent: (limit?: number) => ["documents", "recent", limit] as const,
  status: ["documents", "status"] as const,
};

export function useFormsQuery(query: Partial<FormListQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 6,
    offset: query.offset ?? 0,
    category: query.category,
    search: query.search,
  };
  return useQuery({
    queryKey: documentsKeys.forms(q),
    queryFn: () => documentsApi.forms.list(q),
  });
}

export function useFormCategoriesQuery() {
  return useQuery({
    queryKey: documentsKeys.formCategories,
    queryFn: () => documentsApi.forms.listCategories(),
  });
}

export function useFormDetailQuery(id: string) {
  return useQuery({
    queryKey: documentsKeys.formDetail(id),
    queryFn: () => documentsApi.forms.getById(id),
    enabled: !!id,
  });
}

export function useSubmitFormMutation(id: string) {
  return useMutation({
    mutationFn: (payload: CreateFormSubmissionDTO) =>
      documentsApi.forms.submit(id, payload),
  });
}

export function useTemplatesQuery(query: Partial<TemplateListQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 6,
    offset: query.offset ?? 0,
    departmentId: query.departmentId,
    search: query.search,
  };
  return useQuery({
    queryKey: documentsKeys.templates(q),
    queryFn: () => documentsApi.templates.list(q),
  });
}

export function useContractsQuery() {
  return useQuery({
    queryKey: documentsKeys.contracts,
    queryFn: () => documentsApi.contracts.list(),
  });
}

export function useContractDetailQuery(id: string) {
  return useQuery({
    queryKey: documentsKeys.contractDetail(id),
    queryFn: () => documentsApi.contracts.getById(id),
    enabled: !!id,
  });
}

export function usePoliciesQuery() {
  return useQuery({
    queryKey: documentsKeys.policies,
    queryFn: () => documentsApi.policies.list(),
  });
}

export function usePolicyDetailQuery(id: string) {
  return useQuery({
    queryKey: documentsKeys.policyDetail(id),
    queryFn: () => documentsApi.policies.getById(id),
    enabled: !!id,
  });
}

export function useRecentDocumentsQuery(limit?: number) {
  return useQuery({
    queryKey: documentsKeys.recent(limit),
    queryFn: () => documentsApi.recent(limit),
  });
}

export function useDocumentStatusQuery() {
  return useQuery({
    queryKey: documentsKeys.status,
    queryFn: () => documentsApi.status(),
  });
}
